"use client"

import { useUnifiedWalletContext, useWallet } from "@jup-ag/wallet-adapter"
import type React from "react"
import { useCallback, useEffect, useState } from "react"

import { api } from "../services/api"

// @ts-ignore
import { PublicKey } from "@solana/web3.js"
import { ExternalLink } from "lucide-react"
import "./JupSwap.css"

type DEFAULT_EXPLORER = "SolanaFM"

enum SwapMode {
  ExactInOrOut = "ExactInOrOut",
  ExactIn = "ExactIn",
  ExactOut = "ExactOut",
}

interface FormProps {
  /** Default to `ExactInOrOut`. ExactOut can be used to get an exact output of a token (e.g. for Payments) */
  swapMode?: SwapMode
  /** Initial amount to swap */
  initialAmount?: string
  /** When true, user cannot change the amount (e.g. for Payments) */
  fixedAmount?: boolean
  /** Initial input token to swap */
  initialInputMint?: string
  /** When true, user cannot change the input token */
  fixedInputMint?: boolean
  /** Initial output token to swap */
  initialOutputMint?: string
  /** When true, user cannot change the output token (e.g. to buy your project's token) */
  fixedOutputMint?: boolean
  /** Initial slippage to swap */
  initialSlippageBps?: number
}

const formOptions: FormProps = {
  swapMode: SwapMode.ExactInOrOut,
  initialSlippageBps: 300,
  // @ts-ignore
  referralAccount: '8XKRrTiMErG4sKufg7qyAQtgNywNetMSCpE7VboDCRaf',
  referralFee: 50
}

interface JupSwapProps {
  rpcUrl?: string
  refetchIntervalForTokenAccounts?: number
  formProps?: FormProps
  simulateWalletPassthrough?: boolean
  strictTokenList?: boolean
  defaultExplorer?: DEFAULT_EXPLORER
  useUserSlippage?: boolean
}

const JupSwap: React.FC<JupSwapProps> = (props) => {
  const {
    rpcUrl = "https://lingering-quiet-diamond.solana-mainnet.quiknode.pro/d4b1091c91f157b7a697d950e69df069d7aaeca2",
    formProps = formOptions,
    simulateWalletPassthrough = false,
    strictTokenList = false,
    defaultExplorer = "SolanaFM",
    useUserSlippage = true,
    refetchIntervalForTokenAccounts = 10000,
  } = props

  const [isLoaded, setIsLoaded] = useState(false)
  const passthroughWalletContextState = useWallet()
  const { setShowModal } = useUnifiedWalletContext()

  const launchTerminal = useCallback(async () => {
    const platformFeeAndAccounts = {
      /** Provide referralAccount for automatic referral token account check */
      referralAccount: new PublicKey("8XKRrTiMErG4sKufg7qyAQtgNywNetMSCpE7VboDCRaf"),
      feeBps: 25, // 0.25%
    }

    // @ts-ignore
    window.Jupiter.init({
      displayMode: "integrated",
      integratedTargetId: "integrated-terminal",
      endpoint: rpcUrl,
      refetchIntervalForTokenAccounts,
      formProps,
      enableWalletPassthrough: false,
      passthroughWalletContextState: simulateWalletPassthrough ? passthroughWalletContextState : undefined,
      onRequestConnectWallet: () => setShowModal(true),
      strictTokenList,
      defaultExplorer,
      useUserSlippage,
      platformFeeAndAccounts,
      // @ts-ignore
      onSuccess: async ({ txid, swapResult, quoteResponseMeta }) => {
        console.log("SWAP SUCCESS:")
        console.log({ txid, swapResult, quoteResponseMeta })
        console.log(swapResult.platformFee)
        console.log(passthroughWalletContextState.publicKey?.toBase58())
        const payload = {
          network: "Solana",
          txHash: txid,
          tokenIn: swapResult.inputAddress.toBase58(),
          amountIn: swapResult.inputAmount.toString(),
          tokenOut: swapResult.outputAddress.toBase58(),
          amountOut: swapResult.outputAmount.toString(),
          fee: ((swapResult.inputAmount / 0.9975) * 0.0025).toString()
        }
        await api.post("/swap/", payload)
      },
      // @ts-ignore
      onSwapError: ({ error, quoteResponseMeta }) => {
        console.log("onSwapError", error)
        console.log("onSwapErrorMeta", quoteResponseMeta)
      },
      containerClassName: "jupiter-widget-container",
    })
  }, [
    defaultExplorer,
    formProps,
    passthroughWalletContextState,
    rpcUrl,
    setShowModal,
    simulateWalletPassthrough,
    strictTokenList,
    useUserSlippage,
    refetchIntervalForTokenAccounts,
  ])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined
    // @ts-ignore
    if (!isLoaded || !window.Jupiter.init) {
      intervalId = setInterval(() => {
        // @ts-ignore
        setIsLoaded(Boolean(window.Jupiter.init))
      }, 500)
    }

    if (intervalId) {
      return () => clearInterval(intervalId)
    }
  }, [isLoaded])

  useEffect(() => {
    setTimeout(() => {
      // @ts-ignore
      if (isLoaded && Boolean(window.Jupiter.init)) {
        launchTerminal()
      }
    }, 200)
  }, [isLoaded, simulateWalletPassthrough, props, launchTerminal])

  // Update Jupiter branding
  useEffect(() => {
    let initialInterval: NodeJS.Timeout
    let longInterval: NodeJS.Timeout

    const checkAndUpdateText = () => {
      const spanElement = document.querySelector(".font-bold.text-sm.text-white")
      if (spanElement && spanElement.textContent === "Jupiter") {
        spanElement.textContent = "Powered by Jupiter"
        clearInterval(initialInterval)
        longInterval = setInterval(checkAndUpdateText, 10 * 60 * 1000)
      }
    }

    initialInterval = setInterval(checkAndUpdateText, 10)

    return () => {
      clearInterval(initialInterval)
      clearInterval(longInterval)
    }
  }, [])

  useEffect(() => {
    if (!isLoaded) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const target = mutation.target as HTMLElement

          // Specifically target form elements with "hidden" class
          if (target.tagName === "FORM" && target.classList.contains("hidden")) {
            target.classList.remove("hidden")
          }
        }

        // Check for new elements being added (including modals)
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement

              // Check if a dialog modal was added
              if (element.tagName === "DIALOG" || element.querySelector('dialog[role="dialog"]')) {
                console.log("Dialog modal detected, fixing button styles...")
                setTimeout(fixButtonStyles, 100) // Small delay to ensure modal is fully rendered
              }
            }
          })
        }
      })
    })

    // Function to fix button styles - only when modal is present
    const fixButtonStyles = () => {
      const terminal = document.getElementById("jupiter-terminal-instance")
      if (!terminal) return

      // Check if the specific modal dialog is present and open
      const modalDialog = terminal.querySelector(
        'dialog[role="dialog"][aria-modal="true"][open], dialog.css-1vx73dy[open]',
      )

      if (!modalDialog) {
        console.log("Modal dialog not found or not open, skipping button style fix")
        return
      }

      console.log("Modal dialog found, applying button styles...")

      // Target buttons within the modal specifically
      const buttons = modalDialog.querySelectorAll("button.css-1o7lixs")

      console.log(buttons)
      buttons.forEach((button) => {
        const btn = button as HTMLElement
        btn.style.setProperty("position", "inherit", "important")
        btn.style.setProperty("width", "auto", "important")
        // btn.style.setProperty("max-width", "none", "important")
        // btn.style.setProperty("min-width", "auto", "important")

        console.log("Applied styles to modal button:", btn)
      })

      // Also fix list markers within the modal
      // const listItems = modalDialog.querySelectorAll("li")
      // listItems.forEach((li) => {
        // li.style.setProperty("list-style-type", "none", "important")
      // })
    }

    // Also check for existing hidden forms periodically
    const checkForHiddenForms = () => {
      const terminal = document.getElementById("jupiter-terminal")
      if (terminal) {
        const hiddenForms = terminal.querySelectorAll("form.hidden")
        hiddenForms.forEach((form) => {
          form.classList.remove("hidden")
        })
      }
    }

    // Start observing after a short delay to ensure terminal is loaded
    const timeoutId = setTimeout(() => {
      const terminal = document.getElementById("integrated-terminal")
      if (terminal) {
        observer.observe(terminal, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ["class"],
        })

        // Check immediately and then periodically
        checkForHiddenForms()
        const intervalId = setInterval(checkForHiddenForms, 1000)

        return () => {
          clearInterval(intervalId)
        }
      }
    }, 1000)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [isLoaded])

  // Add event listener for button clicks to fix styles after interactions
  useEffect(() => {
    if (!isLoaded) return

    const handleClick = () => {
      // Short delay to allow DOM to update after click
      setTimeout(() => {
        const terminal = document.getElementById("integrated-terminal")
        if (!terminal) return

        // Target buttons with the specific class
        const buttons = terminal.querySelectorAll(".css-1o7lixs button, .jupiter-terminal-instance.css-1o7lixs button")

        buttons.forEach((button) => {
          const btn = button as HTMLElement
          btn.style.setProperty("position", "inherit", "important")
          btn.style.setProperty("width", "auto", "important")
          btn.style.setProperty("max-width", "none", "important")
          btn.style.setProperty("min-width", "auto", "important")
        })

        // Also fix list markers
        const listItems = terminal.querySelectorAll("li")
        listItems.forEach((li) => {
          li.style.setProperty("list-style-type", "none", "important")
        })
      }, 100)
    }

    const terminal = document.getElementById("integrated-terminal")
    if (terminal) {
      terminal.addEventListener("click", handleClick)
    }

    return () => {
      if (terminal) {
        terminal.removeEventListener("click", handleClick)
      }
    }
  }, [isLoaded])

  return (
    <div className="jupiter-swap-container">
      <div className="swap-header">
        <h1>Swap Tokens</h1>
        <p>Trade Solana tokens with the best rates and lowest fees</p>
      </div>

      <div className="widget-container">
        {!isLoaded && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading Jupiter Terminal...</p>
          </div>
        )}

        <div id="integrated-terminal" className={`jupiter-terminal ${!isLoaded ? "hidden" : ""}`} />
        {/* <div id="integrated-terminal" className={`jupiter-terminal`} /> */}
      </div>

      <div className="swap-footer">
        <div className="powered-by">
          <span>Powered by</span>
          <a href="https://jup.ag" target="_blank" rel="noopener noreferrer" className="jupiter-link">
            Jupiter <ExternalLink className="external-link-icon" />
          </a>
        </div>
      </div>
    </div>
  )
}

export default JupSwap
